import { c as _c } from "react/compiler-runtime";
import { onClearTableSearch } from "@components/common/faro-table/faro-table-filter/filter-toolbar-quick-search";
import { GROUPS_FILTER_COLUMNS } from "@components/groups/groups-filters/groups-filter-constants";
import { MemberHeaders } from "@components/table/members/members-table-utils";
import { PROJECTS_FILTER_COLUMNS } from "@components/projects/projects-filters/projects-filter-constants";
import { ProjectMarkupsTableHeaders } from "@pages/project-details/project-markups/project-markups-table-utils";
import { useAppSelector } from "@store/store-helper";
import { searchSelector } from "@store/ui/ui-selector";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/** Key of the quick filter query param */
export const QUICK_FILTER = "quickFilter";

/** The columns of all the tables that can be filtered, each kept in a query param of its name */
const FILTERABLE_COLUMNS = [...new Set([...Object.values(ProjectMarkupsTableHeaders), MemberHeaders.status, MemberHeaders.role, MemberHeaders.type, MemberHeaders.seats, ...PROJECTS_FILTER_COLUMNS, ...GROUPS_FILTER_COLUMNS])];

/** Whether the query params hold any filter, not considering the quick filter */
export function isAnyFilterAppliedIn(searchParams) {
  return FILTERABLE_COLUMNS.some(column => searchParams.has(column));
}
/** Handles all the logics for filters in query params, including getting and setting the filters */
export function useFilterQueryParams() {
  const $ = _c(27);
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    trackEvent
  } = useTrackEvent();
  const {
    debouncedSearchText
  } = useAppSelector(searchSelector);
  let t0;
  if ($[0] !== searchParams) {
    t0 = isAnyFilterAppliedIn(searchParams);
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isAnyFilterApplied = t0;
  let t1;
  if ($[2] !== searchParams || $[3] !== setSearchParams || $[4] !== trackEvent) {
    t1 = tableType => {
      FILTERABLE_COLUMNS.forEach(column => {
        searchParams.delete(column);
      });
      searchParams.delete(QUICK_FILTER);
      setSearchParams(searchParams);
      onClearTableSearch.emit();
      trackEvent({
        name: `Clear ${tableType} filters`
      });
    };
    $[2] = searchParams;
    $[3] = setSearchParams;
    $[4] = trackEvent;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const clearAllFilters = t1;
  let t2;
  if ($[6] !== searchParams) {
    t2 = filteredColumn => {
      const activeFilterIds = searchParams.getAll(filteredColumn);
      return activeFilterIds;
    };
    $[6] = searchParams;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const getFilterIdsForColumn = t2;
  let t3;
  if ($[8] !== searchParams) {
    t3 = searchParams.get(QUICK_FILTER) ?? "";
    $[8] = searchParams;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const quickFilter = t3;
  let t4;
  let t5;
  if ($[10] !== debouncedSearchText || $[11] !== quickFilter || $[12] !== searchParams || $[13] !== setSearchParams) {
    t4 = () => {
      const newQuickFilter = debouncedSearchText.toLowerCase();
      if (newQuickFilter !== quickFilter) {
        if (newQuickFilter === "") {
          searchParams.delete(QUICK_FILTER);
        } else {
          searchParams.set(QUICK_FILTER, newQuickFilter);
        }
        setSearchParams(searchParams);
      }
    };
    t5 = [debouncedSearchText, quickFilter, searchParams, setSearchParams];
    $[10] = debouncedSearchText;
    $[11] = quickFilter;
    $[12] = searchParams;
    $[13] = setSearchParams;
    $[14] = t4;
    $[15] = t5;
  } else {
    t4 = $[14];
    t5 = $[15];
  }
  useEffect(t4, t5);
  let t6;
  if ($[16] !== getFilterIdsForColumn || $[17] !== searchParams || $[18] !== setSearchParams || $[19] !== trackEvent) {
    t6 = t7 => {
      const {
        tableType: tableType_0,
        filteredColumn: filteredColumn_0,
        filterId,
        shouldReplaceFilterInColumn,
        trackingProps
      } = t7;
      const activeFilterIds_0 = getFilterIdsForColumn(filteredColumn_0);
      let totalFiltersInColumn = activeFilterIds_0.length;
      if (activeFilterIds_0.includes(filterId)) {
        searchParams.delete(filteredColumn_0, filterId);
        totalFiltersInColumn = totalFiltersInColumn - 1;
      } else {
        if (shouldReplaceFilterInColumn) {
          totalFiltersInColumn = 1;
          searchParams.set(filteredColumn_0, filterId);
        } else {
          searchParams.append(filteredColumn_0, filterId);
          totalFiltersInColumn = totalFiltersInColumn + 1;
        }
      }
      trackEvent({
        name: `filter ${tableType_0}`,
        props: {
          filterBy: filteredColumn_0,
          totalFiltersInColumn,
          ...trackingProps
        }
      });
      setSearchParams(searchParams);
    };
    $[16] = getFilterIdsForColumn;
    $[17] = searchParams;
    $[18] = setSearchParams;
    $[19] = trackEvent;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const updateFilterItem = t6;
  let t7;
  if ($[21] !== clearAllFilters || $[22] !== getFilterIdsForColumn || $[23] !== isAnyFilterApplied || $[24] !== quickFilter || $[25] !== updateFilterItem) {
    t7 = {
      isAnyFilterApplied,
      getFilterIdsForColumn,
      quickFilter,
      clearAllFilters,
      updateFilterItem
    };
    $[21] = clearAllFilters;
    $[22] = getFilterIdsForColumn;
    $[23] = isAnyFilterApplied;
    $[24] = quickFilter;
    $[25] = updateFilterItem;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  return t7;
}